import React, { useCallback, useEffect, useRef, useState } from 'react';
import { tr } from '../../i18n';
import './ScrollX.css';

// Horizontal scroll box for wide tables.
//   <ScrollX> <table>…</table> </ScrollX>
// - a normal left/right scrollbar under the table
// - ‹ › buttons on the sides, shown only when there is more to see that way
// - mouse: Shift + wheel, touchpad/phone: swipe, keyboard: click the table then ← →
// Works in Arabic (right-to-left) too.
export default function ScrollX({ children, className = '' }) {
  const boxRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = boxRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 1) { setCanLeft(false); setCanRight(false); return; }
    const rtl = getComputedStyle(el).direction === 'rtl';
    // In RTL, scrollLeft goes from 0 (right end) down to -max (left end).
    const pos = rtl ? max + el.scrollLeft : el.scrollLeft; // 0 = far left
    setCanLeft(pos > 1);
    setCanRight(pos < max - 1);
  }, []);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // Re-check when the table changes size (new data, columns hidden/shown).
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      if (ro) ro.disconnect();
    };
  }, [update, children]);

  const scrollBy = (dir) => {
    const el = boxRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  return (
    <div className={`scrollx ${className}`}>
      {canLeft && (
        <button
          type="button"
          className="scrollx__btn scrollx__btn--left fas fa-chevron-left"
          onClick={() => scrollBy(-1)}
          title={tr('Scroll left')}
          aria-label={tr('Scroll left')}
        />
      )}
      <div className="scrollx__box" ref={boxRef} tabIndex={0}>
        {children}
      </div>
      {canRight && (
        <button
          type="button"
          className="scrollx__btn scrollx__btn--right fas fa-chevron-right"
          onClick={() => scrollBy(1)}
          title={tr('Scroll right')}
          aria-label={tr('Scroll right')}
        />
      )}
    </div>
  );
}
